import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, X, ArrowLeft, ExternalLink } from 'lucide-react';

function DistributionApprovalSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState(searchParams.get('status'));
  const [type, setType] = useState(searchParams.get('type'));

  const isApproved = status === 'approved';
  const isRejected = status === 'rejected';
  const isIpOwner = type === 'ip_owner';
  const isPlatform = type === 'platform';

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className={`px-6 py-8 text-center ${
            isApproved 
              ? 'bg-gradient-to-r from-green-500 to-emerald-500' 
              : 'bg-gradient-to-r from-red-500 to-pink-500'
          }`}>
            <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-4">
              {isApproved ? (
                <CheckCircle className="w-8 h-8 text-white" />
              ) : (
                <X className="w-8 h-8 text-white" />
              )}
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">
              {isApproved ? 'Configuration Approved!' : 'Configuration Rejected'}
            </h1>
            <p className="text-white text-opacity-90">
              {isApproved 
                ? `Thank you for approving as ${isIpOwner ? 'IP Owner' : 'Platform'}`
                : `Configuration rejected by ${isIpOwner ? 'IP Owner' : 'Platform'}`
              }
            </p>
          </div>

          {/* Content */}
          <div className="p-6">
            {isApproved ? (
              <div className="space-y-4">
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                  <h3 className="font-semibold text-green-800 dark:text-green-200 mb-2">
                    What happens next?
                  </h3>
                  <ul className="text-sm text-green-700 dark:text-green-300 space-y-1">
                    <li>• Your approval has been recorded</li>
                    <li>• {isIpOwner ? 'Platform approval' : 'IP Owner approval'} is still needed</li>
                    <li>• Weekly PnL distributions will begin once both parties approve</li>
                    <li>• You'll receive confirmation when fully activated</li>
                  </ul>
                </div>
                
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <h3 className="font-semibold text-blue-800 dark:text-blue-200 mb-2">
                    Distribution Details
                  </h3>
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    Weekly PnL will be distributed automatically every Sunday based on 
                    the approved percentage split. All transactions will be recorded 
                    on the blockchain for transparency.
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                  <h3 className="font-semibold text-red-800 dark:text-red-200 mb-2">
                    Configuration Rejected
                  </h3>
                  <p className="text-sm text-red-700 dark:text-red-300">
                    The distribution configuration has been rejected. The user who 
                    submitted the configuration will be notified and can submit a 
                    new proposal with different terms.
                  </p>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="mt-6 space-y-3">
              <button
                onClick={() => window.close()}
                className={`w-full px-4 py-3 rounded-lg font-medium transition-all duration-200 ${
                  isApproved
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : 'bg-red-600 hover:bg-red-700 text-white'
                } transform hover:scale-105`}
              >
                Close Window
              </button>
              
              <button
                onClick={() => window.location.href = 'https://marketmaker.com'}
                className="w-full px-4 py-3 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors flex items-center justify-center space-x-2"
              >
                <ExternalLink className="w-4 h-4" />
                <span>Visit Market Maker Platform</span>
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Market Maker Platform • Automated Trading & Distribution
          </p>
        </div>
      </div>
    </div>
  );
}

export default DistributionApprovalSuccess;